import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type {
	OsobyFyzickaOsobaDto,
	OsobyKomunikacniUdajeDto,
} from '@gov-nx/api';
import { fileShapeNative, fileShapeWeb } from '@gov-nx/core/hooks';
import {
	LocalizeNameSpaceTypes,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import {
	cleanPhoneNumber,
	formatPhoneNumber,
	getAttachmentsIds,
	useEmailShape,
	usePhoneShape,
} from '@gov-nx/utils/common';
import type { PageCode } from '../definitions/codes';
import type {
	DataPodporaRequest,
	FormData,
	FormPersonData,
	FormProblemData,
} from './context.types';

interface FormPersonInstanceProps {
	code: PageCode;
	communication?: OsobyKomunikacniUdajeDto;
	individualPerson?: OsobyFyzickaOsobaDto;
}

export function FormPersonInstance({
	code,
	communication,
	individualPerson,
}: FormPersonInstanceProps): FormDefinition<FormPersonData> {
	const { getLocalize, getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { getPhoneShape } = usePhoneShape();
	const { getEmailShape } = useEmailShape();

	const formSchema = yup
		.object({
			jmeno: yup.string().required(tsn('formular.validace.zadejte-jmeno')),
			prijmeni: yup
				.string()
				.required(tsn('formular.validace.zadejte-prijmeni')),
			telefon: getPhoneShape(),
			email: getEmailShape({
				isRequired: true,
				requiredMessage: getLocalize(
					LocalizeNameSpaceTypes.Form,
					'validations.zadejte-kontaktni-email'
				),
				invalidShapeMessage: getLocalize(
					LocalizeNameSpaceTypes.Form,
					'validations.email-neni-ve-spravnem-tvaru'
				),
			}),
		})
		.required();
	const formMethods = useGovForm<FormPersonData>({
		formSchema,
		defaultValues: {
			jmeno: individualPerson?.jmeno,
			prijmeni: individualPerson?.prijmeni,
			telefon: communication?.telefonniCislo
				? formatPhoneNumber(communication.telefonniCislo)
				: undefined,
			email: communication?.email,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

interface FormProblemInstanceProps {
	code: PageCode;
	isLoggedIn: boolean;
	isWeb?: boolean;
}

export function FormProblemInstance({
	code,
	isLoggedIn,
	isWeb,
}: FormProblemInstanceProps): FormDefinition<FormProblemData> {
	const { getLocalizeCurried, getLocalize } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { getPhoneShape } = usePhoneShape();
	const { getEmailShape } = useEmailShape();

	const formSchemaWeb = yup
		.object({
			isLogged: yup.boolean().optional(),
			souhrn: yup.string().required(tsn('formular.validace.nazev-problemu')),
			podrobnyPopis: yup
				.string()
				.required(tsn('formular.validace.popis-problemu')),
			vyskytChyby: yup.string().required(tsn('formular.validace.vyskyt-chyby')),
			drivejsiStav: yup
				.string()
				.required(tsn('formular.validace.drivejsi-stav')),
			priloha: yup.array().of(fileShapeWeb()).nullable().optional(),
			zpusobPrihlaseni: yup.string().when('isLogged', ([isLogged]) => {
				if ((isLogged as boolean) === false) {
					return yup
						.string()
						.required(tsn('formular.validace.zpusob-prihlaseni'));
				}
				return yup.string().optional();
			}),
			prihlaseniUserName: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return yup
							.string()
							.required(tsn('formular.validace.prihlaseni-jmeno'));
					}
					return yup.string().optional();
				}),
			prihlaseniEmail: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return getEmailShape({
							isRequired: true,
							requiredMessage: getLocalize(
								LocalizeNameSpaceTypes.Form,
								'validations.zadejte-kontaktni-email'
							),
							invalidShapeMessage: getLocalize(
								LocalizeNameSpaceTypes.Form,
								'validations.email-neni-ve-spravnem-tvaru'
							),
						});
					}
					return yup.string().optional();
				}),
			prihlaseniTelefon: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return getPhoneShape({ isRequired: true });
					}
					return yup.string().optional();
				}),
			prihlaseniIdDS: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'DS') {
						return yup
							.string()
							.required(tsn('formular.validace.prihlaseni-ds'));
					}
					return yup.string().optional();
				}),
			verze: yup.string().optional(),
			build: yup.string().optional(),
			captcha: yup.string().optional(),
			technickeInformace: yup.object().optional(),
		})
		.required();

	const formSchemaNative = yup
		.object({
			isLogged: yup.boolean().optional(),
			souhrn: yup.string().required(tsn('formular.validace.nazev-problemu')),
			podrobnyPopis: yup
				.string()
				.required(tsn('formular.validace.popis-problemu')),
			vyskytChyby: yup.string().required(tsn('formular.validace.vyskyt-chyby')),
			drivejsiStav: yup
				.string()
				.required(tsn('formular.validace.drivejsi-stav')),
			priloha: yup.array().of(fileShapeNative()).nullable().optional(),
			zpusobPrihlaseni: yup.string().when('isLogged', ([isLogged]) => {
				if ((isLogged as boolean) === false) {
					return yup
						.string()
						.required(tsn('formular.validace.zpusob-prihlaseni'));
				}
				return yup.string().optional();
			}),
			prihlaseniUserName: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return yup
							.string()
							.required(tsn('formular.validace.prihlaseni-jmeno'));
					}
					return yup.string().optional();
				}),
			prihlaseniEmail: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return getEmailShape({
							isRequired: true,
							requiredMessage: getLocalize(
								LocalizeNameSpaceTypes.Form,
								'validations.zadejte-kontaktni-email'
							),
							invalidShapeMessage: getLocalize(
								LocalizeNameSpaceTypes.Form,
								'validations.email-neni-ve-spravnem-tvaru'
							),
						});
					}
					return yup.string().optional();
				}),
			prihlaseniTelefon: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'JMENO_HESLO') {
						return getPhoneShape({ isRequired: true });
					}
					return yup.string().optional();
				}),
			prihlaseniIdDS: yup
				.string()
				.when('zpusobPrihlaseni', ([zpusobPrihlaseni]) => {
					if ((zpusobPrihlaseni as string) === 'DS') {
						return yup
							.string()
							.required(tsn('formular.validace.prihlaseni-ds'));
					}
					return yup.string().optional();
				}),
			verze: yup.string().optional(),
			build: yup.string().optional(),
			captcha: yup.string().optional(),
			technickeInformace: yup.object().optional(),
		})
		.required();

	const formMethods = useGovForm<FormProblemData>({
		formSchema: isWeb ? formSchemaWeb : formSchemaNative,
		defaultValues: {
			isLogged: isLoggedIn,
			souhrn: undefined,
			podrobnyPopis: undefined,
			vyskytChyby: undefined,
			drivejsiStav: undefined,
			priloha: undefined,
			zpusobPrihlaseni: undefined,
			prihlaseniUserName: undefined,
			prihlaseniEmail: undefined,
			prihlaseniTelefon: undefined,
			prihlaseniIdDS: undefined,
		},
	});

	return getFormDefinition({
		formMethods,
		formSchema: isWeb ? formSchemaWeb : formSchemaNative,
	});
}

export function FormRecapInstance(): FormDefinition<Record<string, never>> {
	const formSchema = yup.object({}).required();

	const formMethods = useGovForm<Record<string, unknown>>({
		formSchema,
		defaultValues: {},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (data: FormData): DataPodporaRequest => ({
	params: {},
	body: {
		kodPodani: 'SERVICE_DESK',
		jmeno: data.jmeno,
		prijmeni: data.prijmeni,
		email: data.email,
		telefon: data.telefon ? cleanPhoneNumber(data.telefon) : undefined,
		souhrn: data.souhrn,
		podrobnyPopis: data.podrobnyPopis,
		vyskytChyby: data.vyskytChyby,
		drivejsiStav: data.drivejsiStav,
		priloha: data.priloha ? getAttachmentsIds(data.priloha) : undefined,
		zpusobPrihlaseni: data.zpusobPrihlaseni,
		prihlaseniUserName: data.prihlaseniUserName,
		prihlaseniEmail: data.prihlaseniEmail,
		prihlaseniTelefon: data.prihlaseniTelefon
			? cleanPhoneNumber(data.prihlaseniTelefon)
			: undefined,
		prihlaseniIdDS: data.prihlaseniIdDS,
		technickeInformace: JSON.stringify(data.technickeInformace),
		verze: data.verze,
		build: data.build,
		captcha: data.captcha,
	},
});
